import { useState, useEffect, ReactNode, useCallback, useLayoutEffect } from 'react'

import { Breadcrumb, Pagination } from '@arco-design/web-react'
import useLocale from '@/utils/useLocale'
import { SideBar, SpinLoading, Toast, Tag, Space } from 'antd-mobile'
import styles from './index.module.less'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { wrapper } from '@/store'
import type { ProductFiltersRes, ProductsSitemapRes, Res } from '@/types/api'
import { backendCodeProcessor, generateSsrCtx } from '@/api/request'
import getServerSidePropsCatch from '@/utils/getServerSidePropsCatch'
import { SUCCESS_CODE, BLUR_CODE, UNAUTHORIZED_CODE } from '@/utils/constants'
import { getProductFilterApi, getProductSitemapApi } from '@/api/product'
import useRegionOptions from '@/hooks/useRegionOptions'
import { Filter } from '@/types/api'
import { useSetState, useUpdateEffect } from 'ahooks'
import { useSelector } from 'react-redux'
import { StoreState } from '@/store'
import cls from 'classnames'
import { ProductItem, ReginItemProp, getCategoryString, getUrlField, useFooterLinks } from '@/pages/sitemap/products'
export interface Props {
  filtersFromSSR: ProductFiltersRes
  dataFromSSR: Res<ProductsSitemapRes>
}

export const useBreadcrumbs = ({
  currentCategoryObj,
  currentRegionObj
}: {
  currentCategoryObj?: Filter
  currentRegionObj?: ReginItemProp
}) => {
  const t = useLocale()
  return [
    {
      name: t['menu.platform.home'],
      href: '/'
    },
    {
      name: t['seo.sitemap.product_breadcrumb'],
      href: '/products'
    },
    {
      name: currentCategoryObj?.name,
      href: `/sitemap/products/${getCategoryString(currentCategoryObj?.name_slug, currentRegionObj?.nameSlug)}/1`
    },
    {
      name: currentRegionObj?.name,
      href: ''
    }
  ]
}

const ProductsSitemap = ({ filtersFromSSR, dataFromSSR }: Props) => {
  const { product_categories } = filtersFromSSR || {}
  const t = useLocale()
  const [activeCategoryId, setActiveCategoryId] = useState(product_categories[0]?.id)
  const [dataList, setDataList] = useState<ProductsSitemapRes>(dataFromSSR?.data || [])
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const { region } = useSelector((state: StoreState) => state.ssr)
  const { regionOptions } = useRegionOptions()
  const [pageObj, setPageObj] = useSetState({
    total: dataFromSSR.meta?.total || 0,
    pageSize: dataFromSSR.meta?.per_page || 200
  })
  const originRegionObj = regionOptions.find((r) => r.region === region)
  const currentUrlCategory = router.query.products_category

  const currentUrlPage = router.query.page || '1'
  const currentCategoryObj = product_categories.find((c) => c.id === activeCategoryId)

  const currentCategoryNameSlug = currentCategoryObj?.name_slug
  const [currentRegionObj, setCurrentRegionObj] = useState<ReginItemProp | undefined>(originRegionObj)
  const breadcrumbs = useBreadcrumbs({
    currentRegionObj,
    currentCategoryObj
  })
  const { urlCategoryNameSlug, urlRegion } = getUrlField(currentUrlCategory as string)

  const footerList = useFooterLinks({
    category: currentCategoryObj?.name || '',
    region: currentRegionObj?.name || ''
  })

  useLayoutEffect(() => {
    if (urlCategoryNameSlug && urlRegion) {
      const curActiveCategoryId = product_categories.find((c) => c.name_slug === urlCategoryNameSlug)?.id
      curActiveCategoryId && setActiveCategoryId(curActiveCategoryId)
      const curReginObj = regionOptions.find((r) => r.nameSlug === urlRegion)
      curReginObj && setCurrentRegionObj(curReginObj)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchList = useCallback(
    async (params: { category_id: string; page: string; region: string }) => {
      setLoading(true)
      getProductSitemapApi(params)
        .then((res) => {
          if (res.data.code === SUCCESS_CODE) {
            setDataList(res.data.data || [])
            setPageObj({
              total: res.data.meta?.total,
              pageSize: res.data.meta?.per_page
            })
          }
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [setPageObj]
  )

  useUpdateEffect(() => {
    fetchList({
      category_id: activeCategoryId,
      page: currentUrlPage as string,
      region: currentRegionObj?.region || ''
    })
  }, [activeCategoryId, currentRegionObj?.region, currentUrlPage, fetchList])

  const handleReginClick = (item: ReginItemProp) => {
    setCurrentRegionObj(item)

    router.replace(
      {
        pathname: router.basePath,
        query: {
          products_category: getCategoryString(currentCategoryNameSlug, item.nameSlug),
          page: '1'
        }
      },
      undefined,
      { scroll: false, shallow: true }
    )
  }

  return (
    <div className={styles.productsSitemap}>
      <div>
        <Breadcrumb>
          {breadcrumbs.map((item, index) => {
            return (
              <Breadcrumb.Item href={item.href} key={index}>
                {item.name}
              </Breadcrumb.Item>
            )
          })}
        </Breadcrumb>
      </div>
      <h1 style={{ color: '#0f1219' }}>
        {t['seo.sitemap.product_h1']
          .replace('{category}', currentCategoryNameSlug || '')
          .replace('{regionName}', currentRegionObj?.name || '')}
      </h1>
      <div className={styles.container}>
        <div className={styles.side}>
          <SideBar
            activeKey={activeCategoryId}
            onChange={(key) => {
              setActiveCategoryId(key)
            }}
          >
            {product_categories.map((item) => (
              <SideBar.Item
                key={item.id}
                title={
                  <Link
                    href={{
                      pathname: router.pathname,
                      query: {
                        products_category: getCategoryString(item.name_slug, currentRegionObj?.nameSlug),
                        page: '1'
                      }
                    }}
                  >
                    <a>{item.name}</a>
                  </Link>
                }
              />
            ))}
          </SideBar>
        </div>
        <div className={styles.main}>
          <div className={styles.regionWrap}>
            <div className={styles.regionLabel}>{t['common.region']}</div>
            <div className={styles.regionList}>
              {regionOptions.map((option) => {
                return (
                  <div
                    className={cls(styles.regionItem, {
                      [styles.selectedRegion]: currentRegionObj?.region === option.region
                    })}
                    key={option.region}
                    onClick={() => handleReginClick(option)}
                  >
                    <Link
                      href={{
                        pathname: router.pathname,
                        query: {
                          products_category: getCategoryString(currentCategoryNameSlug, option?.nameSlug),
                          page: '1'
                        }
                      }}
                    >
                      <a style={{ pointerEvents: 'none' }}>{option.name}</a>
                    </Link>
                  </div>
                )
              })}
            </div>
          </div>

          <div className={styles.productsSitemapList}>
            {loading ? (
              <SpinLoading />
            ) : (
              dataList.map((item, index) => {
                return <ProductItem item={item} key={index} />
              })
            )}
          </div>
          {!loading && dataList.length > 0 ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Pagination
                total={pageObj.total}
                pageSize={pageObj.pageSize}
                current={currentUrlPage ? +currentUrlPage : 1}
                itemRender={(page: number, type: 'page' | 'more' | 'prev' | 'next', originElement: ReactNode) => {
                  if (type === 'page') {
                    return (
                      <Link
                        href={{
                          pathname: router.pathname,
                          query: {
                            products_category: getCategoryString(currentCategoryNameSlug, currentRegionObj?.nameSlug),
                            page: `${page}`
                          }
                        }}
                      >
                        <a>{page}</a>
                      </Link>
                    )
                  }
                  return originElement
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
      <div>
        {footerList.map((item) => {
          return (
            <div color="primary" key={item.name} className={styles.tagSpan}>
              <Link href={item.href}>
                <a>{item.name}</a>
              </Link>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export const getServerSideProps = wrapper.getServerSideProps<Props>((store) => async (context) => {
  try {
    const ssrCtx = generateSsrCtx(store.getState())
    const [filters] = await Promise.all([getProductFilterApi({ ssrCtx }).then(backendCodeProcessor)])
    const { product_categories } = filters || {}
    const data = await getProductSitemapApi(
      {
        category_id: product_categories[0]?.id,
        page: (context.query.page || '1') as string
      },
      { ssrCtx }
    ).then((res) => {
      if (res.data.code === SUCCESS_CODE) {
        return res.data
      }
    })

    return {
      props: {
        filtersFromSSR: filters,
        dataFromSSR: data
      }
    }
  } catch (error) {
    return getServerSidePropsCatch(error)
  }
})

export default ProductsSitemap
